import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@repo/ui/components/ui/accordion";
import { Button } from "@repo/ui/components/ui/button";
import { cn } from "@repo/ui/lib/utils";
import { ChevronsDownUp, ChevronsUpDown } from "lucide-react";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

/** `single` = one card, sections divided by rules. `cards` = each section is its own card. */
type AccordionVariant = "single" | "cards";

const VariantContext = createContext<AccordionVariant>("single");

interface AccordionControls {
  /** Sections currently mounted, in render order. */
  registered: string[];
  allOpen: boolean;
  multiple: boolean;
  toggleAll: () => void;
  register: (value: string) => () => void;
}

const ControlsContext = createContext<AccordionControls | null>(null);

/**
 * Card-wrapped accordion for detail pages (care plan, case) and section lists
 * (visit charting). Each section's trigger row puts the chevron on the left and
 * an always-visible `actions` slot on the right — the actions render as a sibling
 * of the trigger button, so action clicks never toggle the panel.
 *
 * `variant="single"` (default) renders one bordered card with sections divided by
 * rules; `variant="cards"` renders each section as its own standalone card.
 */
function Root({
  variant = "single",
  multiple = true,
  defaultValue,
  value,
  onValueChange,
  collapsed,
  focusRequest,
  children,
}: {
  variant?: AccordionVariant;
  /** Whether more than one section may be open at once. */
  multiple?: boolean;
  defaultValue?: string[];
  /**
   * Open section ids, when the caller owns the open set — e.g. a page whose
   * expand-all control has to read the real per-section state. Pass with
   * `onValueChange`; `defaultValue` and `focusRequest` are the caller's job then.
   */
  value?: string[];
  /** Fires on user interaction only — not when `defaultValue` or `focusRequest` opens a section. */
  onValueChange?: (value: string[]) => void;
  /**
   * Section ids to fold as they JOIN the set and unfold as they LEAVE it — a
   * procedure whose outcome was just recorded, so the next unanswered card is
   * what the clinician is looking at, and back in play when the outcome is
   * taken back. Only the TRANSITIONS act: in between, the section is the
   * reader's to open and close, which is why this is not a pin.
   */
  collapsed?: string[];
  /** Opens a section in response to navigation from related content. */
  focusRequest?: { value: string; version: number };
  children: ReactNode;
}) {
  const controlled = value !== undefined;
  // Promote to controlled state so newly-added section ids (e.g. a freshly
  // attached questionnaire) can be auto-expanded after mount without tripping
  // Base UI's "uncontrolled value changed" warning. `defaultValue` still
  // seeds the open set on first render; subsequent changes are diffed and
  // any newly-introduced ids are appended to the open list.
  const [openValue, setOpenValue] = useState<string[]>(defaultValue ?? []);
  const seenRef = useRef<Set<string>>(new Set(defaultValue ?? []));
  useEffect(() => {
    if (controlled || !defaultValue) return;
    const additions = defaultValue.filter((v) => !seenRef.current.has(v));
    if (additions.length === 0) return;
    for (const v of additions) seenRef.current.add(v);
    setOpenValue((prev) => [...prev, ...additions]);
  }, [defaultValue, controlled]);
  // Not seeded from the first `collapsed`: a section that arrives already
  // resolved should fold on mount too, not just when it resolves in front of
  // you. `foldedRef` remembers only what THIS folded, so leaving the set
  // reopens a card we closed and never one the reader closed by hand.
  const collapsedKey = (collapsed ?? []).join(" ");
  const collapsedRef = useRef<string[]>(collapsed ?? []);
  collapsedRef.current = collapsed ?? [];
  const foldedRef = useRef<Set<string>>(new Set());
  // `collapsed` is a fresh array every render, so the joined key is the change signal and
  // the array itself is read through a ref.
  // biome-ignore lint/correctness/useExhaustiveDependencies: collapsedKey is that signal
  useEffect(() => {
    if (controlled) return;
    const current = collapsedRef.current;
    const unfolded = [...foldedRef.current].filter((section) => !current.includes(section));
    for (const section of unfolded) foldedRef.current.delete(section);
    const folded = current.filter((section) => !foldedRef.current.has(section));
    for (const section of folded) foldedRef.current.add(section);
    if (folded.length === 0 && unfolded.length === 0) return;
    setOpenValue((previous) => [
      ...previous.filter((section) => !folded.includes(section) && !unfolded.includes(section)),
      ...unfolded,
    ]);
  }, [collapsedKey, controlled]);
  useEffect(() => {
    if (controlled || !focusRequest) return;
    seenRef.current.add(focusRequest.value);
    setOpenValue((previous) =>
      multiple
        ? [...previous.filter((value) => value !== focusRequest.value), focusRequest.value]
        : [focusRequest.value],
    );
  }, [focusRequest, multiple, controlled]);
  const effectiveValue = useMemo(() => {
    const available = value ?? openValue;
    return multiple ? available : available.slice(-1);
  }, [value, openValue, multiple]);

  // Sections register themselves so an expand-all control knows the full set
  // without the caller having to restate it.
  const [registered, setRegistered] = useState<string[]>([]);
  const register = useCallback((section: string) => {
    setRegistered((previous) => (previous.includes(section) ? previous : [...previous, section]));
    return () => setRegistered((previous) => previous.filter((entry) => entry !== section));
  }, []);
  const allOpen = registered.length > 0 && registered.every((section) => effectiveValue.includes(section));
  const toggleAll = useCallback(() => {
    const next = allOpen ? [] : registered;
    // Remembering collapsed ids keeps the `defaultValue` effect from re-opening
    // them the next time its array identity changes.
    for (const section of registered) seenRef.current.add(section);
    onValueChange?.(next);
    if (!controlled) setOpenValue(next);
  }, [allOpen, registered, onValueChange, controlled]);
  const controls = useMemo<AccordionControls>(
    () => ({ registered, allOpen, multiple, toggleAll, register }),
    [registered, allOpen, multiple, toggleAll, register],
  );

  return (
    <VariantContext.Provider value={variant}>
      <ControlsContext.Provider value={controls}>
        <Accordion
          value={effectiveValue}
          onValueChange={(v) => {
            const next = v as string[];
            onValueChange?.(next);
            if (!controlled) setOpenValue(next);
          }}
          multiple={multiple}
          className={variant === "single" ? "rounded-lg border bg-card" : "gap-3"}
        >
          {children}
        </Accordion>
      </ControlsContext.Provider>
    </VariantContext.Provider>
  );
}

/**
 * Expands every section, or collapses every section once they are all open.
 * Renders nothing when the accordion only allows one open section at a time.
 */
function ExpandAll() {
  const controls = useContext(ControlsContext);
  if (!controls || !controls.multiple || controls.registered.length < 2) return null;
  const label = controls.allOpen ? "Collapse all" : "Expand all";
  return (
    <Button variant="ghost" size="icon-xs" aria-label={label} title={label} onClick={controls.toggleAll}>
      {controls.allOpen ? <ChevronsDownUp /> : <ChevronsUpDown />}
    </Button>
  );
}

interface SectionProps {
  /** Optional DOM anchor for links from related charting content. */
  id?: string;
  /** Unique id for this section, used by the accordion's open state. */
  value: string;
  title: ReactNode;
  /** Always-visible trailing actions — rendered beside the trigger, not inside it. */
  actions?: ReactNode;
  /** Rendered below the panel, outside it, so it stays visible when the section is collapsed. */
  footer?: ReactNode;
  /**
   * Keeps the panel's children mounted while closed, so state inside them — a
   * nested collapsible's open flag — survives the section being folded.
   */
  keepMounted?: boolean;
  children: ReactNode;
}

interface HeaderSectionProps {
  title: ReactNode;
  /** Always-visible trailing actions. */
  actions?: ReactNode;
  children?: ReactNode;
}

/** Non-collapsible heading and optional summary content within the accordion container. */
function HeaderSection({ title, actions, children }: HeaderSectionProps) {
  const variant = useContext(VariantContext);
  return (
    <div
      data-slot="detail-accordion-header-section"
      className={cn("px-5 not-last:border-b", variant === "cards" && "rounded-lg border bg-card")}
    >
      <div className="flex items-center gap-2 py-4">
        <div className="flex-1 text-sm font-semibold">{title}</div>
        {actions != null && <div className="flex shrink-0 items-center">{actions}</div>}
      </div>
      {children != null && <div className="pb-4 text-sm">{children}</div>}
    </div>
  );
}

function Section({ id, value, title, actions, footer, keepMounted, children }: SectionProps) {
  const variant = useContext(VariantContext);
  const register = useContext(ControlsContext)?.register;
  useEffect(() => register?.(value), [register, value]);
  return (
    <AccordionItem id={id} value={value} className={cn("px-5", variant === "cards" && "rounded-lg border bg-card")}>
      <AccordionTrigger actions={actions}>{title}</AccordionTrigger>
      <AccordionContent keepMounted={keepMounted}>{children}</AccordionContent>
      {footer != null && <div className="pb-4">{footer}</div>}
    </AccordionItem>
  );
}

/**
 * Optional content row — a single item with a left rule. Consecutive Items
 * space themselves, so no wrapper is needed.
 */
function Item({ children }: { children: ReactNode }) {
  return <div className="border-l-2 border-muted-foreground/30 py-0.5 pl-2.5 not-first:mt-3">{children}</div>;
}

/** Standard empty-state message for a section with no content. */
function Empty({ children }: { children: ReactNode }) {
  return <p className="py-2 text-center text-xs text-muted-foreground">{children}</p>;
}

export const DetailAccordion = Object.assign(Root, { HeaderSection, Section, Item, Empty, ExpandAll });
